import { Typography, theme } from "antd"
import React from "react"

const { Text } = Typography

interface GuideConstructionVisualProps {
  className?: string
  /** Short handwritten-style copy overlaid on the illustration, e.g.
   * "Build smarter. Together." Omit for a plain, unlabeled illustration. */
  tagline?: string
  /** Which edge the illustration fades toward — the side that borders real
   * content, so the line art visually recedes behind it. */
  fadeFrom?: "left" | "top"
}

/**
 * A small, hand-built line-art illustration (blueprint grid + a
 * partially-framed building + a crane) rather than a stock photo — this
 * repo has no construction/building imagery to reuse, and the visual spec
 * explicitly asks not to introduce an unrelated stock-photo dependency.
 * Purely decorative: no props here read or affect any onboarding state.
 */
export const GuideConstructionVisual = ({
  className,
  tagline,
  fadeFrom = "left"
}: GuideConstructionVisualProps) => {
  const { token } = theme.useToken()

  return (
    <div className={`relative overflow-hidden ${className ?? ""}`}>
      <svg
        viewBox="0 0 400 300"
        preserveAspectRatio="xMidYMid slice"
        className="size-full"
        fill="none"
        aria-hidden="true"
      >
        <g stroke={token.colorBorderSecondary} strokeWidth="1">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <line key={`h${i}`} x1="0" y1={i * 40} x2="400" y2={i * 40} />
          ))}
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
            <line key={`v${i}`} x1={i * 44} y1="0" x2={i * 44} y2="300" />
          ))}
        </g>
        <g stroke={token.colorPrimary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.55">
          <path d="M90 260 V120 H210 V260" />
          <path d="M90 120 L150 85 L210 120" />
          <path d="M90 260 H210" />
          <path d="M118 260 V150 M146 260 V130 M174 260 V150" />
          <path d="M90 175 H210 M90 215 H210" />
          <path d="M235 260 V55 M235 55 L330 78 M235 92 L292 105 M292 105 V150" />
          <circle cx="235" cy="50" r="4" fill={token.colorPrimary} stroke="none" />
        </g>
      </svg>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            fadeFrom === "left"
              ? `linear-gradient(90deg, ${token.colorBgContainer} 0%, rgba(255,255,255,0) 45%)`
              : `linear-gradient(180deg, ${token.colorBgContainer} 0%, rgba(255,255,255,0) 40%)`
        }}
      />
      {tagline && (
        <Text
          className="absolute bottom-5 left-5 text-base italic"
          style={{ color: token.colorPrimary }}
        >
          {tagline}
        </Text>
      )}
    </div>
  )
}
